import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import {
  Plus,
  Edit,
  Trash2,
  FileText,
  Eye,
  EyeOff,
  Wand2,
  Search,
  Filter,
  FileJson,
  Users,
  Crown,
  BarChart3,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Navbar from "../components/Navbar";
import AIContentStudio from "../components/AIContentStudio";
import ManualContentEditor from "../components/ManualContentEditor";
import BulkImportExport from "../components/BulkImportExport";
import UserManagement from "../components/UserManagement";
import OwnershipRoles from "../components/OwnershipRoles";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import { toast } from "sonner";

const AdminPage = () => {
  const navigate = useNavigate();
  const { token, isAdmin, isOwner, isSuperAdmin } = useAuth();

  const [passages, setPassages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [activeTab, setActiveTab] = useState("passages");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchPassages();
  }, [isAdmin]);

  const fetchPassages = async () => {
    try {
      const response = await axios.get(`${API}/admin/passages`, {
        headers,
        withCredentials: true,
      });
      setPassages(response.data);
    } catch (error) {
      console.error("Error fetching passages:", error);
      toast.error("Failed to load passages");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePassage = async (passageId) => {
    if (!window.confirm("Delete this passage and all its questions?")) return;
    try {
      await axios.delete(`${API}/admin/passages/${passageId}`, {
        headers,
        withCredentials: true,
      });
      toast.success("Passage deleted");
      setSelectedPassage(null);
      fetchPassages();
    } catch (error) {
      toast.error("Failed to delete passage");
    }
  };

  const handleSelectPassage = (passage) => {
    setSelectedPassage(passage);
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleNewPassage = () => {
    setSelectedPassage(null);
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleEditPassage = () => {
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleSaved = async () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedPassage(null);
    // Small delay to ensure database writes are committed
    await new Promise(resolve => setTimeout(resolve, 500));
    await fetchPassages();
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
  };

  // Handle navigation from Bulk Import to Passages tab with filters
  const handleNavigateToPassages = (status, difficulty) => {
    setStatusFilter(status || "");
    setDifficultyFilter(difficulty ? String(difficulty) : "");
    setActiveTab("passages");
    // Refresh passages list
    fetchPassages();
  };

  // Filter passages
  const filteredPassages = passages.filter((p) => {
    const matchesSearch =
      !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    const matchesDifficulty = !difficultyFilter || p.difficulty === parseInt(difficultyFilter);
    return matchesSearch && matchesStatus && matchesDifficulty;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "published":
        return <Badge className="bg-emerald-100 text-emerald-700">Published</Badge>;
      case "review":
        return <Badge className="bg-amber-100 text-amber-700">In Review</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Draft</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Content Management</h1>
            <p className="text-slate-600">Create and manage passages and questions</p>
          </div>
        </div>

        {/* Main Admin Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="passages" className="gap-2" data-testid="passages-tab">
              <FileText className="w-4 h-4" />
              Passages ({passages.length})
            </TabsTrigger>
            <TabsTrigger value="ai-studio" className="gap-2" data-testid="ai-studio-tab">
              <Wand2 className="w-4 h-4" />
              AI Content Studio
            </TabsTrigger>
            <TabsTrigger value="bulk-import" className="gap-2" data-testid="bulk-import-tab">
              <FileJson className="w-4 h-4" />
              Bulk Import/Export
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2" data-testid="users-tab">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            {(isSuperAdmin || isOwner) && (
              <TabsTrigger value="analytics" className="gap-2" data-testid="analytics-tab">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span>Analytics</span>
              </TabsTrigger>
            )}
            {isOwner && (
              <TabsTrigger value="ownership" className="gap-2 bg-amber-50" data-testid="ownership-tab">
                <Crown className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700">Ownership & Roles</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Passages Tab */}
          <TabsContent value="passages">
            {isCreating || isEditing ? (
              <ManualContentEditor
                existingPassage={isEditing ? selectedPassage : null}
                onSaved={handleSaved}
                onCancel={handleCancel}
              />
            ) : (
              <div className="space-y-6">
                {/* Quick Actions Bar */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-700">Quick Actions:</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button 
                      onClick={handleNewPassage}
                      className="gap-2"
                      data-testid="create-manual-passage-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create Manual Passage
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setActiveTab("ai-studio")}
                      className="gap-2"
                      data-testid="open-ai-studio-btn"
                    >
                      <Wand2 className="w-4 h-4" />
                      AI Content Studio
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setActiveTab("bulk-import")}
                      className="gap-2"
                      data-testid="open-bulk-import-btn"
                    >
                      <FileJson className="w-4 h-4" />
                      Bulk Import JSON
                    </Button>
                  </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                {/* Passages List */}
                <Card className="lg:col-span-1">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-3">
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Passages ({filteredPassages.length})
                      </CardTitle>
                      <Button size="sm" onClick={handleNewPassage} data-testid="new-passage-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        New
                      </Button>
                    </div>

                    {/* Search and Filter */}
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search passages..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                          <SelectTrigger className="w-full" data-testid="status-filter">
                            <SelectValue placeholder="All Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="review">In Review</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={difficultyFilter || "all"} onValueChange={(v) => setDifficultyFilter(v === "all" ? "" : v)}>
                          <SelectTrigger className="w-full" data-testid="difficulty-filter">
                            <SelectValue placeholder="All Levels" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Levels</SelectItem>
                            <SelectItem value="1">Level 1</SelectItem>
                            <SelectItem value="2">Level 2</SelectItem>
                            <SelectItem value="3">Level 3</SelectItem>
                            <SelectItem value="4">Level 4</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(statusFilter || difficultyFilter) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { setStatusFilter(""); setDifficultyFilter(""); }}
                          className="text-xs w-full"
                        >
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[55vh]">
                      <div className="space-y-2 pr-4">
                        {filteredPassages.map((passage) => (
                          <div
                            key={passage.passage_id}
                            onClick={() => handleSelectPassage(passage)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${
                              selectedPassage?.passage_id === passage.passage_id
                                ? "bg-slate-900 text-white"
                                : "bg-white hover:bg-slate-100 border border-slate-200"
                            }`}
                            data-testid={`passage-item-${passage.passage_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{passage.title}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <Badge
                                    variant={
                                      selectedPassage?.passage_id === passage.passage_id
                                        ? "secondary"
                                        : "outline"
                                    }
                                    className="text-xs"
                                  >
                                    {passage.category}
                                  </Badge>
                                  <span className="text-xs opacity-70">
                                    {passage.question_count} Q
                                  </span>
                                  <span className="text-xs opacity-70">
                                    L{passage.difficulty}
                                  </span>
                                </div>
                              </div>
                              {passage.status === "published" ? (
                                <Eye className="w-4 h-4 opacity-50 flex-shrink-0" />
                              ) : (
                                <EyeOff className="w-4 h-4 opacity-50 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        ))}
                        {filteredPassages.length === 0 && (
                          <div className="text-center py-8 text-slate-500">
                            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p>No passages found</p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Passage Details */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>
                        {selectedPassage ? "Passage Details" : "Select a Passage"}
                      </CardTitle>
                      {selectedPassage && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleEditPassage}
                            data-testid="edit-passage-btn"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeletePassage(selectedPassage.passage_id)}
                            data-testid="delete-passage-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedPassage ? (
                      <div className="space-y-4">
                        {/* Status & Meta */}
                        <div className="flex flex-wrap gap-2">
                          {getStatusBadge(selectedPassage.status)}
                          <Badge variant="outline">{selectedPassage.category}</Badge>
                          <Badge variant="outline">Level {selectedPassage.difficulty}</Badge>
                          <Badge variant="outline">{selectedPassage.content_type}</Badge>
                          {selectedPassage.exam_modes?.map((mode) => (
                            <Badge key={mode} variant="outline" className="capitalize">
                              {mode}
                            </Badge>
                          ))}
                        </div>

                        {/* Title */}
                        <h2 className="text-xl font-semibold text-slate-900">
                          {selectedPassage.title}
                        </h2>

                        {/* Content Preview */}
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm font-medium text-slate-500 mb-2">
                            Passage Content
                          </p>
                          <ScrollArea className="h-[150px]">
                            <p className="text-slate-700 whitespace-pre-wrap font-serif">
                              {selectedPassage.content}
                            </p>
                          </ScrollArea>
                        </div>

                        {/* Summary */}
                        {selectedPassage.summary && (
                          <div className="bg-sky-50 rounded-xl p-4">
                            <p className="text-sm font-medium text-sky-700 mb-1">Summary</p>
                            <p className="text-slate-700">{selectedPassage.summary}</p>
                          </div>
                        )}

                        {/* Vocabulary */}
                        {selectedPassage.vocabulary?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-slate-500 mb-2">
                              Vocabulary
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {selectedPassage.vocabulary.map((v, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 bg-white border rounded-lg text-sm"
                                >
                                  <strong>{v.word}</strong>: {v.meaning}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Questions Count */}
                        <div className="flex items-center justify-between p-4 bg-slate-100 rounded-xl">
                          <div>
                            <p className="font-medium text-slate-900">
                              {selectedPassage.question_count} Questions
                            </p>
                            <p className="text-sm text-slate-500">
                              Click Edit to manage questions
                            </p>
                          </div>
                          <Button onClick={handleEditPassage}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Passage & Questions
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-16">
                        <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500 mb-4">
                          Select a passage to view details
                        </p>
                        <p className="text-slate-400 text-sm mb-6">or</p>
                        <Button onClick={handleNewPassage}>
                          <Plus className="w-4 h-4 mr-2" />
                          Create New Passage
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              </div>
            )}
          </TabsContent>

          {/* AI Content Studio Tab */}
          <TabsContent value="ai-studio">
            <AIContentStudio />
          </TabsContent>

          {/* Bulk Import/Export Tab */}
          <TabsContent value="bulk-import">
            <BulkImportExport 
              passages={passages} 
              onImportComplete={fetchPassages}
              onNavigateToPassages={handleNavigateToPassages}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          {/* Analytics Tab - SUPER_ADMIN and OWNER only */}
          {(isSuperAdmin || isOwner) && (
            <TabsContent value="analytics">
              <AnalyticsDashboard />
            </TabsContent>
          )}

          {/* Ownership & Roles Tab - OWNER only */}
          {isOwner && (
            <TabsContent value="ownership">
              <OwnershipRoles />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
